import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {DateTime} from 'luxon';
import {DisplayOption} from '@/components/interfaces/ComponentOptions';

export interface CalendarItem {
    id: number;
    startDateTime: DateTime;
    text: string;
    data: any;
    start?: string;
    legendKey?: any;
}

export interface CalendarLegend {
    key: string;
    value: string;
    color: string;
    backgroundColor: string;
}

interface Day {
    month: number;
    day: number;
    showAll: false;
    items: CalendarItem[];
}

interface Week {
    days: Day[];
}

@Component({
    filters: {
        longMonth(month: number) {
            return DateTime.local(2000, month).toFormat('MMMM');
        },
        shortMonth(month: number) {
            return DateTime.local(2000, month).toFormat('MMM');
        }
    }
})
export default class Calendar extends Vue {
    @Prop({ type: Array, required: true })
    private items!: CalendarItem[];
    @Prop({ type: Boolean, required: false, default: true })
    private shouldDisplay!: boolean;
    @Prop({ type: String, required: false, default: '' })
    private rightText!: string;
    @Prop({ type: String, required: true })
    private path!: string;
    // use function as `default` value for array prop to prevent from getting the following error:
    // "Props with type Object/Array must use a factory function to return the default value"
    @Prop({ type: Array, required: false, default:  () => [] })
    private legends!: CalendarLegend[];

    private monthSelectorActive = false;

    private get showLegend(): Boolean {
        return this.legends.length > 0 && this.weeks.length > 0;
    }

    private monthEnumeration: DisplayOption[] = [
        { id: 1, name: DateTime.local(2000, 1).toFormat('MMM') },
        { id: 2, name: DateTime.local(2000, 2).toFormat('MMM') },
        { id: 3, name: DateTime.local(2000, 3).toFormat('MMM') },
        { id: 4, name: DateTime.local(2000, 4).toFormat('MMM') },
        { id: 5, name: DateTime.local(2000, 5).toFormat('MMM') },
        { id: 6, name: DateTime.local(2000, 6).toFormat('MMM') },
        { id: 7, name: DateTime.local(2000, 7).toFormat('MMM') },
        { id: 8, name: DateTime.local(2000, 8).toFormat('MMM') },
        { id: 9, name: DateTime.local(2000, 9).toFormat('MMM') },
        { id: 10, name: DateTime.local(2000, 10).toFormat('MMM') },
        { id: 11, name: DateTime.local(2000, 11).toFormat('MMM') },
        { id: 12, name: DateTime.local(2000, 12).toFormat('MMM') },
    ];

    private months: DisplayOption[] = [
        ...this.monthEnumeration,
        ...this.monthEnumeration,
        ...this.monthEnumeration,
        ...this.monthEnumeration,
        ...this.monthEnumeration,
        ...this.monthEnumeration,
        ...this.monthEnumeration,
        ...this.monthEnumeration,
        ...this.monthEnumeration,
        ...this.monthEnumeration,
    ];
    private years: DisplayOption[] = [];

    private currentMonth = DateTime.local().setZone('America/Chicago').month;
    private currentYear = DateTime.local().setZone('America/Chicago').year;
    private weeks: Week[] = [];

    private showFromHover = false;
    private showFromClick = false;
    private eventInformationStyles: any = {};
    private isBrowserMSEdge = false;

    get showEventInformation(): boolean {
        return this.showFromHover || this.showFromClick;
    }

    @Watch('items')
    public itemsChanged() {
        if (this.shouldDisplay) {
            this.buildCalendar();
        }
    }

    private buildCalendar() {
        const dates = this.getMonthDates();
        let dateTime = dates.start;

        this.weeks = [];
        let currentWeek: Week = { days: [] };
        const items = this.getItemsForDay();
        while (dateTime.startOf('day') <= dates.end.startOf('day')) {
            if (dateTime.weekday === 7) {
                currentWeek = { days: [] };
                this.weeks.push(currentWeek);
            }
            currentWeek.days.push({
                month: dateTime.month,
                day: dateTime.day,
                showAll: false,
                items: items.get(dateTime.toFormat('MM/dd/yyyy')) || []
            });
            dateTime = dateTime.plus({ day: 1 });
        }
    }

    /*
     * Only want to iterate the items once in case there are hundreds of items.
     * Also, this method tries to use Luxon as little as possible (e.g. it compares dates by strings) because Luxon is slow
     * and it really adds up when there are hundreds of events
     * The initial naive implementation spent 1.5 seconds in this method when there were 235 events. It now takes about 20ms
     */
    private getItemsForDay(): Map<string, CalendarItem[]> {
        const map = new Map<string, CalendarItem[]>();
        if (this.items.length === 0) {
            return map;
        }
        let date = this.items[0].startDateTime.toFormat('MM/dd/yyyy');
        let array: CalendarItem[] = [];
        map.set(date, array);
        for (const item of this.items) {
            const currentDate = item.startDateTime.toFormat('MM/dd/yyyy');
            if (currentDate !== date) {
                map.set(date, array);
                date = currentDate;
                array = [];
            }
            const hour = item.startDateTime.toFormat('h');
            const minutes = item.startDateTime.minute > 0 ? ':' + item.startDateTime.toFormat('mm') : '';
            const amPm = (item.startDateTime.hour >= 12 ? 'p' : 'a');
            array.push({
                id: item.id,
                startDateTime: item.startDateTime,
                text: item.text,
                start: hour + minutes + amPm,
                data: item.data,
                legendKey: item.legendKey
            });
        }
        map.set(date, array);
        return map;
    }


    @Watch('shouldDisplay', { immediate: true })
    public shouldDisplayChanged() {
        this.loadData();
    }

    public created() {
        this.isBrowserMSEdge = window.navigator.userAgent.indexOf("Edge") > -1;
        this.gotoToday();
    }

    private loadData() {
        if (this.shouldDisplay) {
            this.$emit('load-data');
        }
    }

    private setYears() {
        this.years = [];
        this.years.push(
            { id: this.currentYear - 4, name: String(this.currentYear - 4) },
            { id: this.currentYear - 3, name: String(this.currentYear - 3) },
            { id: this.currentYear - 2, name: String(this.currentYear - 2) },
            { id: this.currentYear - 1, name: String(this.currentYear - 1) },
            { id: this.currentYear, name: String(this.currentYear) },
            { id: this.currentYear + 1, name: String(this.currentYear + 1) },
            { id: this.currentYear + 2, name: String(this.currentYear + 2) },
            { id: this.currentYear + 3, name: String(this.currentYear + 3) },
            { id: this.currentYear + 4, name: String(this.currentYear + 4) }
        );
    }

    gotoToday() {
        const now = DateTime.local().setZone('America/Chicago');
        this.currentMonth = now.month;
        this.currentYear = now.year;
        this.setYears();
        this.loadData();
    }

    previousMonth() {
        this.monthSelected(this.currentMonth - 1);
    }

    nextMonth() {
        this.monthSelected(this.currentMonth + 1);
    }

    monthSelected(month: number) {
        if (month === 0) {
            this.currentMonth = 12;
            this.currentYear -= 1;
        } else if (month === 13) {
            this.currentMonth = 1;
            this.currentYear += 1;
        } else {
            this.currentMonth = month;
        }
        this.loadData();
    }

    yearSelected(year: number) {
        this.currentYear = year;
        this.setYears();
        this.loadData();
    }

    public getMonthDates(): { start: DateTime, end: DateTime } {
        let dateTime = DateTime.fromObject({ year: this.currentYear, month: this.currentMonth, day: 1, zone: 'America/Chicago' });
        if (dateTime.weekday !== 7) {
            dateTime = dateTime.startOf('week').minus({ day: 1 });
        }
        const start = dateTime;

        while (dateTime.year < this.currentYear ||
        (dateTime.year === this.currentYear && dateTime.month <= this.currentMonth && dateTime.day <= dateTime.daysInMonth) ||
        dateTime.weekday <= 6) {
            dateTime = dateTime.plus({ day: 1 });
        }
        return { start, end: dateTime.minus({ second: 1 }) };
    }

    eventMouseOver (item: CalendarItem, event: any) {
        if (event.target.className.indexOf('event') !== -1) {
            this.$emit('item-hovered', item);
            this.updateStyles(event);
        }
        this.showFromHover = true;
    }

    private updateStyles(event: MouseEvent) {
        const maxWidth = 300;
        const element = (event.currentTarget as HTMLElement);
        const rect = element.getBoundingClientRect();
        const parent = (event.currentTarget as HTMLElement).offsetParent as HTMLElement;
        let shiftElementX = element.offsetLeft;
        let shiftElementY = element.offsetTop;
        let eventElementWidth = element.offsetWidth;
        const alignRight = parent.clientWidth - rect.left < maxWidth;
        const alignAbove = parent.clientHeight - shiftElementY < maxWidth * 2;
        const infoWidth = document.getElementById('event-information')!.offsetWidth;
        const infoHeight = document.getElementById('event-information')!.offsetHeight;
        const top = alignAbove ? shiftElementY - infoHeight - 5 : shiftElementY + element.offsetHeight + 5;
        const left = alignRight ? shiftElementX + eventElementWidth - infoWidth : shiftElementX;
        this.eventInformationStyles = {
            top: top + 'px',
            left: left + 'px',
        };
    }

    eventMouseLeave () {
        this.showFromHover = false;
    }

    eventMouseMove (event: any) {
        this.updateStyles(event);
    }

    buildPath(id: number) {
        return '#';
    }

    getLegendStyle ({ legendKey: key }: CalendarItem) {
        const singleLegend = this.legends.filter(item => item.key === key);

        if (singleLegend.length === 1) {
            const [{color, backgroundColor}] = singleLegend;
            if ( color && backgroundColor ) {
                return {color, backgroundColor};
            }
        }
        return {}
    }

    cancel() {
        this.showFromClick = false;
    }

    itemClicked(item: any) {
        this.showFromClick = !this.showFromClick;
        if (this.showFromClick) {
            this.$emit('item-hovered', item);
        }
    }
}
